import React, { useContext, useEffect, useMemo } from 'react'

import { WithOptimizelyProps, withOptimizely } from '@optimizely/react-sdk'
import { Div, PageWrapper, SectionContainer, Grid, Box } from '@vividseats/vivid-ui-kit'
import { useRouter } from 'next/router'

import MainLayout from '@/components/layouts/main-layout'
import Breadcrumbs from '@/components/shared/breadcrumbs'
import HeaderCardImage from '@/components/shared/header-card-image'
import Title from '@/components/shared/sub-category/components/title'
import { getMetadataForSubCategory } from '@/components/shared/sub-category/utils'
import { ACCESSIBILITY_IDS } from '@/constants'
import SubCategoryContext from '@/context/sub-category'
import { useTrackPageView } from '@/hooks/use-analytics'
import { OPTIMIZELY_EVENT } from '@/optimizely/constants'
import { Metadata, PageType } from '@/types/app-types'

import styles from './styles.module.scss'
import type { SubCategoryLayoutProps } from './type'

const SubCategoryLayout: React.FC<SubCategoryLayoutProps & WithOptimizelyProps> = ({
    mainContent,
    rightRailContent,
    topContent,
    optimizely,
}) => {
    const router = useRouter()

    const {
        state: { fetching },
        selectors: { subCategoryParams, subCategory, breadcrumbs },
    } = useContext(SubCategoryContext)

    const metadata: Metadata | undefined = useMemo(() => getMetadataForSubCategory(subCategory), [subCategory])

    useTrackPageView(PageType.SubCategory, {
        category_id: subCategory?.categoryId ?? null,
        subcategory_id: subCategory?.id ?? null,
        subcategory: subCategory?.shortName ?? null,
    })

    useEffect(() => {
        optimizely?.track(OPTIMIZELY_EVENT.categoryPageView)
    }, [])

    if (fetching) {
        return null
    }

    if (!subCategory) {
        router.push(`/404?sub-category=${subCategoryParams.webPath}`)
        return null
    }

    return (
        <MainLayout metadata={metadata} white>
            <div id={ACCESSIBILITY_IDS.CONTENT} />
            <PageWrapper white withMobileGutters>
                <SectionContainer className={styles.sectionContainer}>
                    <Div marginTop={11} data-testid="title">
                        <Grid>
                            <Box paddingLeft={1} paddingRight={1}>
                                <Breadcrumbs
                                    links={breadcrumbs}
                                    pageTitle={subCategory.name}
                                    pageURL={subCategory.webPath}
                                />
                            </Box>
                            <Box>
                                <Title title={subCategory.name} />
                            </Box>
                        </Grid>
                    </Div>
                    <Div marginTop={7} data-testid="header-card-image">
                        <HeaderCardImage
                            mobileImageUrl={subCategory.mobileImageUrl}
                            desktopImageUrl={subCategory.desktopImageUrl}
                            externalImageMidPathKey="SUBCATEGORY"
                            externalImageFilePath={subCategory.externalImageFilePath}
                        />
                    </Div>
                    <Div desktopDisplay="flex" marginTop={11}>
                        <div className={styles.contentWrapper}>
                            {topContent ? (
                                <div>
                                    <Div desktopFlexBasis={12} data-testid="top-content">
                                        {topContent}
                                    </Div>
                                </div>
                            ) : null}
                            {(mainContent || rightRailContent) && (
                                <div className={styles.sideBySideWrapper}>
                                    {mainContent ? (
                                        <Div
                                            desktopFlexBasis={0}
                                            grow
                                            desktopMarginRight={14}
                                            marginBottom={11}
                                            data-testid="main-content"
                                        >
                                            {mainContent}
                                        </Div>
                                    ) : null}
                                    {rightRailContent ? (
                                        <Div desktopFlexBasis={4} shrink marginBottom={11} data-testid="right-rail">
                                            {rightRailContent}
                                        </Div>
                                    ) : null}
                                </div>
                            )}
                        </div>
                    </Div>
                </SectionContainer>
            </PageWrapper>
        </MainLayout>
    )
}

export default withOptimizely(SubCategoryLayout)
